import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, IconButton, Tooltip } from '@c2fo/liquidity';
import { PenIcon, ShapesDuotoneIcon, ShapesIcon, TrashCanIcon } from '@c2fo/liquidity/icons';
import { RecurringRuleCategory, TakerMarketWithRecurringRule } from '@/enterprise/data/useRecurringRules';
import RecurringRulesForm, {
  RecurringRulesFormReferrerType,
} from '@/enterprise/features/recurringRules/RecurringRulesForm';
import { mapRecurringRuleToFormCriteria } from '@/enterprise/features/recurringRules/utils/utils';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import Divider from '@/shared/components/Divider';
import useRestrictions from '@/utils/useRestrictions';
import DeleteRuleModal from './DeleteRuleModal';
import RuleChips from './RuleChips';

const MiniRuleManager = ({
  asChild = false,
  referrer,
  takerMarketWithRule,
}: {
  asChild?: boolean;
  referrer: RecurringRulesFormReferrerType;
  takerMarketWithRule: TakerMarketWithRecurringRule;
}) => {
  const { rule, takerMarket } = takerMarketWithRule;
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { getRestrictions } = useRestrictions();
  const { canEditRecurringRules } = getRestrictions([takerMarket]);
  const [openRuleModal, setOpenRuleModal] = useState(false);
  const [openDeleteRuleModal, setOpenDeleteRuleModal] = useState(false);

  const referrerForTrackEvent = referrer === 'rulesManager' ? 'rules-manager' : 'division-details';

  const handleOpenRuleModal = () => {
    setOpenRuleModal(true);
    trackEnterpriseEvent('recurring-rules::opened', {
      referrer: referrerForTrackEvent,
      source: 'mini-rule-manager',
    });
  };

  const handleCloseRuleModal = () => {
    setOpenRuleModal(false);
    trackEnterpriseEvent('recurring-rules::closed');
  };

  const handleDeleteAllRulesModalOpen = () => {
    setOpenDeleteRuleModal(true);
  };

  const handleDeleteAllRulesModalClose = () => {
    setOpenDeleteRuleModal(false);
  };

  return (
    <>
      <RecurringRulesForm
        isForSingleDivision
        mode="edit"
        onClose={handleCloseRuleModal}
        open={openRuleModal}
        referrer={referrer}
        takerMarkets={[takerMarket]}
        defaultValues={{
          invoiceRules: Object.entries(mapRecurringRuleToFormCriteria(rule)).map(([key, value]) => ({
            category: key as RecurringRuleCategory,
            criteria: value,
          })),
          operatorType: rule.operatorType,
          takerMarkets: [takerMarket],
        }}
      />
      <DeleteRuleModal
        onClose={handleDeleteAllRulesModalClose}
        open={openDeleteRuleModal}
        referrer={referrerForTrackEvent}
        takerMarketWithRule={takerMarketWithRule}
      />
      <IntercomDataTarget
        target="invoiceAutomationMiniRulesManger"
        className={cn('flex items-center gap-4 rounded-2xl bg-lightBlue-50 p-4', {
          'rounded bg-canvas': asChild,
        })}
      >
        <div className="flex-1">
          {asChild ? (
            <div className="flex items-center gap-4">
              {referrer === 'rulesManager' && (
                <ShapesIcon className="h-9 w-9 fill-gray-700" title={t('automation.invoiceRules')} />
              )}
              <RuleChips bold recurringRule={rule} withLabel />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <ShapesDuotoneIcon
                  className="hidden h-5 w-5 fill-blue-500 sm:inline-block"
                  title={t('automation.invoiceRules')}
                />
                <div className="font-medium">{t('automation.invoiceRules')}</div>
              </div>
              <div className="sm:ml-7">
                <RuleChips bold inline recurringRule={rule} variant="dark" withLabel />
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Tooltip>
            <Tooltip.Trigger asChild>
              <IconButton
                disabled={!canEditRecurringRules}
                icon={PenIcon}
                name={t('recurringRules.editRule')}
                onClick={handleOpenRuleModal}
                size="sm"
                variant="ancillary"
              />
            </Tooltip.Trigger>
            <Tooltip.Content arrow side="left" variant="dark" className="max-w-60 whitespace-pre-wrap text-center">
              {t('automation.editInvoiceRules')}
            </Tooltip.Content>
          </Tooltip>
          <Divider vertical />
          <IconButton
            className="bg-transparent"
            disabled={!canEditRecurringRules}
            icon={TrashCanIcon}
            inverse
            name={t('recurringRules.removeRule')}
            onClick={handleDeleteAllRulesModalOpen}
            size="sm"
            variant="destructive"
          />
        </div>
      </IntercomDataTarget>
    </>
  );
};

export default MiniRuleManager;
